import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Input } from "../ui/Input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/Dialog";
import { GitCompare, Search, X, ChevronDown, ChevronUp } from "lucide-react";
import { getTemplateDisplayTime } from "./timeUtils";

const CHECKBOX_CLASS =
  "relative h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-[4px] border-2 border-ktx-purple bg-white transition-colors checked:bg-ktx-purple after:absolute after:left-[5px] after:top-[1px] after:h-[10px] after:w-[6px] after:rotate-45 after:border-b-2 after:border-r-2 after:border-white after:opacity-0 after:content-[''] checked:after:opacity-100 disabled:cursor-not-allowed disabled:opacity-40";

// Search-driven setup for comparing two templates. The user searches for a
// template by name, its card appears with version checkboxes, and they pick two
// versions total (from one or two different templates). Selections persist as
// the search term changes, so the two picks can come from different templates.
const TemplateCompareSetup = ({
  open,
  onOpenChange,
  groupedTemplateEntries,
  formatTimestamp,
  onCompare,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState([]);
  // Names of groups whose non-default versions are expanded/visible.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSelected([]);
      setExpandedGroups(new Set());
    }
  }, [open]);

  const toggleExpand = useCallback((name) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const isSelected = useCallback(
    (template) =>
      selected.some(
        (s) => s.name === template.name && s.version === template.version
      ),
    [selected]
  );

  const toggle = useCallback((template) => {
    setSelected((prev) => {
      const idx = prev.findIndex(
        (s) => s.name === template.name && s.version === template.version
      );
      if (idx !== -1) return prev.filter((_, i) => i !== idx);
      if (prev.length >= 2) return prev;
      return [...prev, template];
    });
  }, []);

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return groupedTemplateEntries.filter(([name]) =>
      name.toLowerCase().includes(term)
    );
  }, [searchTerm, groupedTemplateEntries]);

  const canCompare = selected.length === 2;

  const handleCompare = () => {
    if (!canCompare) return;
    onCompare(selected[0], selected[1]);
  };

  // Renders a single version row. `trailing` (optional) is placed at the far
  // right of the row, outside the clickable label (e.g. the expand toggle).
  const renderVersionRow = (template, trailing = null) => {
    const checked = isSelected(template);
    const disabled = !checked && selected.length >= 2;
    const displayTime = getTemplateDisplayTime(template);
    const isDefault = template.is_default === true;
    return (
      <div
        key={`${template.name}-${template.version}`}
        className={`flex items-center justify-between gap-3 p-2 rounded-md border ${
          checked
            ? "border-ktx-purple bg-purple-50"
            : isDefault
            ? "border-purple-200 bg-purple-50/40"
            : "border-gray-200"
        } ${disabled ? "opacity-50" : ""}`}
      >
        <label
          className={`flex items-center gap-3 min-w-0 flex-1 ${
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={() => toggle(template)}
            className={CHECKBOX_CLASS}
          />
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2">
              {isDefault && (
                <Badge
                  variant="outline"
                  className="text-xs font-semibold border-purple-200 bg-purple-50 text-ktx-purple"
                >
                  Default
                </Badge>
              )}
              <span className="font-medium text-gray-900">
                {template.version || "v1.0"}
              </span>
            </div>
            {(template.username || displayTime) && (
              <div className="text-xs text-gray-400 mt-0.5">
                {template.username && (
                  <span className="font-medium text-gray-600">
                    {template.username}
                  </span>
                )}
                {template.username && displayTime && <span> · </span>}
                {displayTime && formatTimestamp(displayTime)}
              </div>
            )}
          </div>
        </label>
        {trailing}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-3">
            <GitCompare className="h-6 w-6 shrink-0" />
            Compare Templates
          </DialogTitle>
          <DialogDescription className="text-gray-600 mt-1">
            Search for a template, then pick two versions to compare (they can be
            from different templates).
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            autoFocus
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search templates by name..."
            className="pl-9"
          />
        </div>

        {/* Current picks */}
        {selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500">Selected:</span>
            {selected.map((t) => (
              <span
                key={`${t.name}-${t.version}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-ktx-purple bg-purple-50 px-3 py-1 text-sm text-ktx-purple"
              >
                <span className="font-medium">{t.name}</span>
                <span className="text-gray-500">·</span>
                <span>{t.version || "v1.0"}</span>
                <button
                  type="button"
                  onClick={() => toggle(t)}
                  className="ml-1 text-ktx-purple/70 hover:text-ktx-purple"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
          {!searchTerm.trim() ? (
            <div className="text-center text-gray-400 py-10">
              Start typing to find a template.
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center text-gray-400 py-10">
              No templates match "{searchTerm}".
            </div>
          ) : (
            filteredGroups.map(([name, versions]) => {
              const defaultVersion =
                versions.find((v) => v.is_default === true) || versions[0];
              const otherVersions = versions.filter(
                (v) => v !== defaultVersion
              );
              const expanded = expandedGroups.has(name);
              const toggleControl =
                otherVersions.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => toggleExpand(name)}
                    title={
                      expanded
                        ? "Hide other versions"
                        : `Show ${otherVersions.length} other version${
                            otherVersions.length > 1 ? "s" : ""
                          }`
                    }
                    className="shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-ktx-purple hover:bg-purple-50"
                  >
                    {expanded ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        Hide
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        {otherVersions.length} more
                      </>
                    )}
                  </button>
                ) : null;
              return (
                <div
                  key={name}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="font-medium text-gray-900 mb-2">{name}</div>
                  <div className="space-y-2">
                    {renderVersionRow(defaultVersion, toggleControl)}
                    {expanded && otherVersions.map((v) => renderVersionRow(v))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="flex items-center sm:justify-between">
          <span className="text-sm text-gray-500">
            {selected.length}/2 selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCompare}
              disabled={!canCompare}
              className={`flex items-center gap-2 font-medium border ${
                canCompare
                  ? "border-ktx-purple bg-purple-50 text-ktx-purple hover:bg-purple-100 hover:border-ktx-purple"
                  : "border-gray-300 bg-white text-gray-400 opacity-50 cursor-not-allowed"
              }`}
            >
              <GitCompare className="h-4 w-4" />
              Compare
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCompareSetup;
